import { FC, ReactNode, FormEvent, useState, useEffect } from 'react';
import { Root, Trigger } from '@radix-ui/react-dialog';
import {
  Root as FormRoot,
  Field,
  Label,
  Message,
  Control,
} from '@radix-ui/react-form';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Dialog, Input } from '../../components';
import strings from '../../language/Swedish';
import usePasswordReset from '@/api/mutations/usePasswordReset';

const StyledFormRoot = styled(FormRoot)`
  ${tw`w-full`};
`;

const StyledField = styled(Field)`
  ${tw`grid mb-4 text-slate-600 has-[:focus]:text-primary`};
`;

const StyledInput = styled(Input)`
  ${tw`box-border w-full bg-white/70 inline-flex h-10 appearance-none items-center justify-center rounded-lg px-3 leading-none text-slate-800 border border-slate-300 hover:border-slate-600 outline-none transition shadow-[0_0_0_0_rgb(var(--color-primary)_/_0)] focus:shadow-[0_0_0_4px_rgb(var(--color-primary)_/_0.2)] focus:bg-white focus:border-primary selection:bg-slate-200`};
`;

const StyledMessage = styled(Message)`
  ${tw`text-xs text-red-500 overflow-hidden`};
`;

const MessageContainer = styled.div`
  ${tw`flex items-baseline justify-between mb-1`};
`;

interface Props {
  children?: ReactNode;
  setIsResetPassword: (val:boolean) => void;
}

const validateEmail = (email:string):RegExpMatchArray | null => {
  // eslint-disable-next-line
  return email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};

const ForgotPassword: FC<Props> = ({ children, setIsResetPassword }) => {

  const [username, setUsername] = useState("");
  const [error, setError] = useState(" ");
  const [open, setOpen] = useState(false);
  const { isError, error: mutationError, mutate: forgotPasswordMutation } = usePasswordReset();

  useEffect(() => {
    if(isError && mutationError){
      // Handle actual network/HTTP errors (not API error responses)
      const apiError = mutationError as any;
      const errorMessage = apiError?.response?.data?.message || apiError?.message || strings.data.somethingWentWrong;
      setError(errorMessage);
    }
  },[isError, mutationError]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation(); 
    if (username.length < 2) {
      setError(strings.login.emailMessage1);
      return ;
    } else if (!validateEmail(username)) {
      setError(strings.login.emailMessage2);
      return;
    }

    forgotPasswordMutation(
      username,
      {
        onSuccess: (response:any) => {
          // Response can be either success or error JSON
          // Error: {"status":"error","message":"..."}
          // Success: some other structure
          if (response && response.status === "error" && response.message) {
            // Check for specific error code and show custom message
            if (response.message === "ERROR_NO_PASSWORD_CHANGE_PERMISSION") {
              setError("Ditt konto saknar behörighet för att återställa lösenord. Kontakta info@valueguard.se för mer information.");
            } else {
              setError(response.message);
            }
          } else {
            setUsername("");
            setError(" ");
            setIsResetPassword(true);
            setOpen(false);
          }
        },
      }
    );
  }

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger asChild>
        <div>{children}</div>
      </Trigger>
      <Dialog
        title={strings.login.passwordRequest}
        description={strings.login.passwordDescription}
        bottomButton={{ type: 'submit', text: strings.login.passwordRequest, onClick: (e) => handleSubmit(e)}}>
        <StyledFormRoot onSubmit={handleSubmit}>
          <StyledField name="E-post">
            <Label>{strings.login.email}</Label>
            <StyledMessage>
              {error}
            </StyledMessage>
            <Control asChild>
              <StyledInput type="email" value={username} autoComplete="off" required onChange={(e) => setUsername(e.target.value)} />
            </Control>
          </StyledField>
        </StyledFormRoot>
      </Dialog>
    </Root>
  )
};

export default ForgotPassword;
